import { GL, GLSL_COMMON, Program } from '../gl';
import { ALT_MIN, LUT_H, LUT_W } from '../atmosphere';
import { SkyTables } from '../skytables';
import { clamp, smoothstep } from '../../lib/math';

const VS = /* glsl */ `#version 300 es
layout(location = 0) in vec2 a_pos;
out vec2 v_ndc;
void main() { v_ndc = a_pos; gl_Position = vec4(a_pos, 0.0, 1.0); }`;

// Inverse projection shared by the full-screen passes.
const INVERSE = /* glsl */ `
vec3 camDirFromNdc(vec2 ndc) {
  vec2 p = ndc / (u_scale * u_aspectFix);
  if (u_proj == 0) return normalize(vec3(p, -1.0));
  float r = length(p);
  float th = 2.0 * atan(r * 0.5);
  vec2 d2 = r > 1e-6 ? p / r : vec2(0.0);
  return vec3(sin(th) * d2, -cos(th));
}
// Apparent → true altitude (Bennett), for sampling fixed sky textures.
vec3 unrefractEnu(vec3 d) {
  float a = degrees(asin(clamp(d.z, -1.0, 1.0)));
  if (a < -1.5) return d;
  float ha = max(a, -1.0);
  float R = (1.0 / tan(radians(ha + 7.31 / (ha + 4.4)))) / 60.0;
  float a2 = radians(a - R);
  float h = length(d.xy);
  if (h < 1e-6) return d;
  return vec3(d.xy * (cos(a2) / h), sin(a2));
}
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
`;

const SKY_FS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
${INVERSE}
in vec2 v_ndc;
out vec4 o;
uniform sampler2D u_lut;      // rows 0..H-1: sun, rows H..2H-1: moon
uniform sampler2D u_mw;
uniform sampler2D u_band;   // the sky in another kind of light (radio, infrared…), same layout as u_mw
uniform float u_bandMix;
uniform vec3 u_sunEnu;
uniform vec3 u_moonEnu;
uniform float u_moonScale;    // moon irradiance relative to the sun
uniform float u_mwGain;
uniform vec3 u_nightBase;     // display-space colour of a dark sky at the zenith
uniform vec3 u_lpColor;       // display-space light-pollution glow at the horizon
uniform float u_transparent;  // 1 when drawing over the camera feed
uniform float u_exposure;
uniform vec2 u_tabScale;

const float PI = 3.14159265;
const float ALT_MIN = ${ALT_MIN.toFixed(4)};

vec3 sampleLut(vec3 d, vec3 L, float row0) {
  float alt = asin(clamp(d.z, -1.0, 1.0));
  float v = sqrt(clamp((alt - ALT_MIN) / (PI * 0.5 - ALT_MIN), 0.0, 1.0));
  vec2 dh = d.xy, lh = L.xy;
  float az = 0.0;
  if (length(dh) > 1e-5 && length(lh) > 1e-5) az = acos(clamp(dot(normalize(dh), normalize(lh)), -1.0, 1.0));
  float u = az / PI;
  float y = (row0 + (0.5 + v * ${LUT_H - 1}.0)) / ${2 * LUT_H}.0;
  float x = (0.5 + u * ${LUT_W - 1}.0) / ${LUT_W}.0;
  return texture(u_lut, vec2(x, y)).rgb;
}

// Per-channel exposure tone mapping (the exposure models partial eye adaptation, set on the CPU).
vec3 displayMap(vec3 L) {
  return vec3(1.0) - exp(-L * u_exposure);
}

void main() {
  vec3 cam = camDirFromNdc(v_ndc);
  vec3 d = transpose(u_enuToCam) * cam;       // apparent direction
  vec3 dt = unrefractEnu(d);                   // true direction

  vec3 L = sampleLut(d, u_sunEnu, 0.0) * u_tabScale.x;
  if (u_moonScale > 0.0) L += sampleLut(d, u_moonEnu, ${LUT_H}.0) * (u_moonScale * u_tabScale.y);
  vec3 col = displayMap(L);
  float dayness = clamp(dot(col, vec3(0.333)) * 3.0, 0.0, 1.0);

  // Night floor: deep blue zenith, light pollution glowing from the horizon.
  float h = max(d.z, 0.0);
  vec3 night = u_nightBase * (0.55 + 0.45 * exp(-h * 3.0));
  night += u_lpColor * exp(-h * 5.5);
  col = max(col, night) + night * 0.25;

  // Milky Way (fixed to the stars), dimmed by twilight, moonlight and air mass.
  if (u_mwGain > 0.0) {
    vec3 e = transpose(u_eqjToEnu) * dt;
    float ra = atan(e.y, e.x);
    float dec = asin(clamp(e.z, -1.0, 1.0));
    vec2 uv = vec2(fract(0.5 - ra / (2.0 * PI)), 0.5 - dec / PI);
    vec3 mw = pow(texture(u_mw, uv).rgb, vec3(2.4)) * 0.45;
    float airmass = 1.0 / (max(dt.z, 0.0) + 0.025 * exp(-11.0 * max(dt.z, 0.0)));
    float ext = exp(-0.28 * (airmass - 1.0));
    col += mw * u_mwGain * ext * (1.0 - dayness);
  }

  // Another wavelength, shown over the whole sky (the images already carry their display curve).
  if (u_bandMix > 0.0) {
    vec3 e = transpose(u_eqjToEnu) * dt;
    float ra = atan(e.y, e.x);
    float dec = asin(clamp(e.z, -1.0, 1.0));
    vec2 uv = vec2(fract(0.5 - ra / (2.0 * PI)), 0.5 - dec / PI);
    col = mix(col, texture(u_band, uv).rgb, u_bandMix * 0.94);
  }

  // Dither to hide banding in the dark gradients.
  col += (hash12(gl_FragCoord.xy) - 0.5) / 255.0;
  o = u_transparent > 0.5 ? vec4(0.0) : vec4(col, 1.0);
}`;

// Ground drawn after the sky objects, hiding everything below the landscape silhouette.
const GROUND_FS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
${INVERSE}
in vec2 v_ndc;
out vec4 o;
uniform vec3 u_groundColor;   // display space, from the current sky brightness
uniform vec3 u_rimColor;
uniform float u_groundAlpha;
uniform float u_landscape;    // 0 flat horizon, 1 hills and trees

// Periodic 1-D value noise: k cells around the full circle, so there is no seam at north.
float n1p(float x, float k) {
  float t = x * k;
  float i = floor(t), f = fract(t);
  float a = hash12(vec2(mod(i, k), 1.7)), b = hash12(vec2(mod(i + 1.0, k), 1.7));
  return mix(a, b, f * f * (3.0 - 2.0 * f));
}

// Skyline height (degrees above the true horizon) at an azimuth: rolling hills, woods and lone trees.
float hills(float az) {
  float x = fract(az / 6.2831853);
  float h = 1.5 * n1p(x, 7.0) + 0.7 * n1p(x, 19.0) + 0.25 * n1p(x, 61.0) - 0.55;
  h = max(h, 0.12);
  float forest = smoothstep(0.5, 0.78, n1p(x + 0.37, 13.0));
  float k = 1100.0;
  float t = x * k;
  float cell = mod(floor(t), k);
  float f = fract(t);
  float r1 = hash12(vec2(cell, 3.7)), r2 = hash12(vec2(cell, 9.1));
  float w = 0.32 + 0.3 * r1;
  float crown = sqrt(max(0.0, 1.0 - pow((f - 0.5) / w, 2.0)));
  float trees = forest * (0.25 + 0.75 * r2) * crown * (0.8 + 0.5 * n1p(x, 47.0));
  if (forest < 0.05 && r1 > 0.965) trees = (0.35 + 0.55 * r2) * crown;
  return h + trees;
}

void main() {
  vec3 cam = camDirFromNdc(v_ndc);
  vec3 d = transpose(u_enuToCam) * cam;
  float alt = degrees(asin(clamp(d.z, -1.0, 1.0)));
  float az = atan(d.x, d.y);
  float edge = u_landscape > 0.5 ? hills(az) : 0.0;
  float pxDeg = 114.59 / (u_scale * min(u_viewport.x, u_viewport.y)); // degrees per pixel near the centre
  float k = smoothstep(edge + pxDeg, edge - pxDeg, alt);
  if (k <= 0.0) discard;
  float depth = clamp(-alt / 12.0, 0.0, 1.0);
  vec3 col = mix(u_groundColor * 1.25, u_groundColor * 0.45, depth);
  // Soft glow along the skyline.
  col += u_rimColor * exp(-max(edge - alt, 0.0) * 1.8) * 0.35;
  col += (hash12(gl_FragCoord.xy) - 0.5) / 255.0;
  o = vec4(col * k, k) * u_groundAlpha;
}`;

// ---------- The same sky on the CPU ----------
// Labels choose dark or light text from how bright the sky right behind them is drawn, so this mirrors
// SKY_FS and GROUND_FS: scattering tables, exposure, night floor, light pollution and the ground. Left out:
// the Milky Way (faint), the dither, and other wavelengths, whose images are taken to be dark.

/** What the last frame drew, as the shaders saw it. */
export interface SkyShade {
  lut: Float32Array; // LUT_W × 2·LUT_H RGBA, normalised: the Sun's table, then the Moon's
  sunEnu: ArrayLike<number>;
  moonEnu: ArrayLike<number>;
  sunK: number; // u_tabScale.x
  moonK: number; // u_moonScale × u_tabScale.y, 0 with the Moon down
  exposure: number;
  nightBase: ArrayLike<number>;
  lpColor: ArrayLike<number>;
  bandMix: number;
  ground: { color: ArrayLike<number>; rim: ArrayLike<number>; alpha: number; landscape: boolean } | null;
}

/** Add one scattering table's radiance in direction d, lit from L, times k (sampleLut). */
function addLut(lut: Float32Array, d: ArrayLike<number>, L: ArrayLike<number>, row0: number, k: number, out: number[]) {
  const alt = Math.asin(clamp(d[2], -1, 1));
  const v = Math.sqrt(clamp((alt - ALT_MIN) / (Math.PI / 2 - ALT_MIN), 0, 1));
  const dh = Math.hypot(d[0], d[1]), lh = Math.hypot(L[0], L[1]);
  const az = dh > 1e-5 && lh > 1e-5 ? Math.acos(clamp((d[0] * L[0] + d[1] * L[1]) / (dh * lh), -1, 1)) : 0;
  // The texture coordinates land on texel centres at both ends, so linear filtering is plain bilinear.
  const fx = (az / Math.PI) * (LUT_W - 1), fy = v * (LUT_H - 1);
  const i0 = Math.min(Math.floor(fx), LUT_W - 2), j0 = Math.min(Math.floor(fy), LUT_H - 2);
  const tx = fx - i0, ty = fy - j0;
  const a = ((row0 + j0) * LUT_W + i0) * 4, b = a + LUT_W * 4;
  for (let c = 0; c < 3; c++) {
    const top = lut[a + c] + (lut[a + 4 + c] - lut[a + c]) * tx;
    const bot = lut[b + c] + (lut[b + 4 + c] - lut[b + c]) * tx;
    out[c] += (top + (bot - top) * ty) * k;
  }
}

const f32 = Math.fround;
const fract = (x: number) => x - Math.floor(x);
const mod = (a: number, b: number) => a - b * Math.floor(a / b);
/** hash12 from the shaders, in single precision like the GPU. */
function hash12(x: number, y: number) {
  const a = fract(f32(x * 0.1031)), b = fract(f32(y * 0.1031));
  const s = f32(a * f32(b + 33.33) + b * f32(a + 33.33) + a * f32(a + 33.33));
  const px = f32(a + s), py = f32(b + s);
  return fract(f32(f32(px + py) * px));
}
function n1p(x: number, k: number) {
  const t = f32(x * k), i = Math.floor(t), f = t - i;
  const a = hash12(mod(i, k), 1.7), b = hash12(mod(i + 1, k), 1.7);
  return a + (b - a) * f * f * (3 - 2 * f);
}
/** Height of the hills and trees (degrees) at an azimuth (radians), as GROUND_FS draws them. */
export function skyline(az: number): number {
  const x = fract(az / 6.2831853);
  const h = Math.max(1.5 * n1p(x, 7) + 0.7 * n1p(x, 19) + 0.25 * n1p(x, 61) - 0.55, 0.12);
  const forest = smoothstep(0.5, 0.78, n1p(x + 0.37, 13));
  const t = x * 1100, cell = mod(Math.floor(t), 1100), f = fract(t);
  const r1 = hash12(cell, 3.7), r2 = hash12(cell, 9.1);
  const crown = Math.sqrt(Math.max(0, 1 - ((f - 0.5) / (0.32 + 0.3 * r1)) ** 2));
  let trees = forest * (0.25 + 0.75 * r2) * crown * (0.8 + 0.5 * n1p(x, 47));
  if (forest < 0.05 && r1 > 0.965) trees = (0.35 + 0.55 * r2) * crown;
  return h + trees;
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const LUMA = [0.2126, 0.7152, 0.0722];

/** WCAG relative luminance (0–1) of the sky, or the ground over it, drawn in an apparent ENU direction. */
export function skyLuminance(s: SkyShade, d: ArrayLike<number>): number {
  const L = [0, 0, 0];
  addLut(s.lut, d, s.sunEnu, 0, s.sunK, L);
  if (s.moonK > 0) addLut(s.lut, d, s.moonEnu, LUT_H, s.moonK, L);
  const h = Math.max(d[2], 0);
  const k3 = 0.55 + 0.45 * Math.exp(-h * 3), k5 = Math.exp(-h * 5.5);
  const g = s.ground;
  const alt = (Math.asin(clamp(d[2], -1, 1)) * 180) / Math.PI;
  // The hills stay below 3.5°, and well under them the skyline only tints the ground's rim glow.
  const edge = !g ? -90 : g.landscape && alt > -2 && alt < 3.5 ? skyline(Math.atan2(d[0], d[1])) : 0;
  const depth = clamp(-alt / 12, 0, 1), rim = Math.exp(-Math.max(edge - alt, 0) * 1.8) * 0.35;
  let y = 0;
  for (let c = 0; c < 3; c++) {
    const night = s.nightBase[c] * k3 + s.lpColor[c] * k5;
    let col = (Math.max(1 - Math.exp(-L[c] * s.exposure), night) + night * 0.25) * (1 - 0.94 * s.bandMix);
    if (g && alt < edge) col = (g.color[c] * (1.25 - 0.8 * depth) + g.rim[c] * rim) * g.alpha + col * (1 - g.alpha);
    y += LUMA[c] * toLinear(clamp(col, 0, 1));
  }
  return y;
}

export class SkyLayer {
  private sky: Program;
  private ground: Program;
  private vao: WebGLVertexArrayObject;
  private lut: WebGLTexture;
  private lutData = new Float32Array(LUT_W * LUT_H * 2 * 4);
  /** The last frame's sky, so labels can ask how bright it is behind them. */
  private shade: SkyShade = {
    lut: this.lutData, sunEnu: [0, 0, 1], moonEnu: [0, 0, 1], sunK: 0, moonK: 0, exposure: 1, nightBase: [0, 0, 0], lpColor: [0, 0, 0], bandMix: 0, ground: null,
  };
  private groundShade = { color: [0, 0, 0] as ArrayLike<number>, rim: [0, 0, 0] as ArrayLike<number>, alpha: 1, landscape: false };
  private overCamera = false;
  private sunAltKey = NaN;
  private moonAltKey = NaN;
  // Tables are stored normalised (max = 1) so half floats keep precision in deep twilight.
  private tabScale = [0, 0];
  mwTex: WebGLTexture | null = null;
  bandTex: WebGLTexture | null = null;
  bandMix = 0;

  constructor(private gl: GL) {
    this.sky = new Program(gl, VS, SKY_FS, 'sky');
    this.ground = new Program(gl, VS, GROUND_FS, 'ground');
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.lut = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.lut);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, LUT_W, LUT_H * 2, 0, gl.RGBA, gl.FLOAT, this.lutData);
  }

  private tables = new SkyTables();
  private raw = new Float32Array(LUT_W * LUT_H * 4);
  private tablesVersion = -1;

  /** Update the scattering tables when the Sun or Moon altitude changes (cached and blended; see SkyTables). */
  updateLights(sunAlt: number, moonAlt: number, moonUp: boolean) {
    const sk = sunAlt > -0.35 ? Math.round(sunAlt * 5000) / 5000 : -9; // ~0.01°
    const mk = moonUp ? Math.round(moonAlt * 2000) / 2000 : -9;
    const fresh = this.tables.version !== this.tablesVersion;
    this.tablesVersion = this.tables.version;
    let dirty = false;
    if (sk !== this.sunAltKey || fresh) {
      this.sunAltKey = sk;
      this.tabScale[0] = this.store(sk !== -9 && this.tables.blend(sk, this.raw, 0) ? this.raw : null, 0);
      dirty = true;
    }
    if (mk !== this.moonAltKey || fresh) {
      this.moonAltKey = mk;
      this.tabScale[1] = this.store(mk !== -9 && this.tables.blend(mk, this.raw, 0) ? this.raw : null, LUT_W * LUT_H * 4);
      dirty = true;
    }
    if (dirty) {
      const gl = this.gl;
      gl.bindTexture(gl.TEXTURE_2D, this.lut);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, LUT_W, LUT_H * 2, 0, gl.RGBA, gl.FLOAT, this.lutData);
    }
  }

  private store(table: Float32Array | null, offset: number): number {
    const n = LUT_W * LUT_H * 4;
    if (!table) {
      this.lutData.fill(0, offset, offset + n);
      return 0;
    }
    let max = 1e-30;
    for (let i = 0; i < n; i++) if ((i & 3) !== 3 && table[i] > max) max = table[i];
    for (let i = 0; i < n; i++) this.lutData[offset + i] = (i & 3) === 3 ? 1 : table[i] / max;
    return max;
  }

  /** Green-channel radiance at the zenith for the Sun table and the Moon table (unit irradiance). */
  zenith(): { sun: number; moon: number } {
    const row = (LUT_H - 1) * LUT_W * 4;
    return { sun: this.lutData[row + 1] * this.tabScale[0], moon: this.lutData[LUT_W * LUT_H * 4 + row + 1] * this.tabScale[1] };
  }

  /** WCAG relative luminance of the last frame in an apparent ENU direction (0 over the camera feed, which is unknown). */
  luminance(d: ArrayLike<number>): number {
    return this.overCamera ? 0 : skyLuminance(this.shade, d);
  }

  drawSky(setCommon: (p: Program) => void, u: {
    sunEnu: ArrayLike<number>; moonEnu: ArrayLike<number>; moonScale: number; mwGain: number;
    nightBase: ArrayLike<number>; lpColor: ArrayLike<number>; transparent: boolean; exposure: number; sunScale?: number;
  }) {
    const gl = this.gl;
    const p = this.sky.use();
    setCommon(p);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.lut);
    p.i1('u_lut', 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.mwTex);
    p.i1('u_mw', 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.bandTex);
    p.i1('u_band', 2).f1('u_bandMix', this.bandTex ? this.bandMix : 0);
    p.v3('u_sunEnu', u.sunEnu).v3('u_moonEnu', u.moonEnu).f1('u_moonScale', u.moonScale);
    p.f1('u_mwGain', this.mwTex ? u.mwGain : 0).v3('u_nightBase', u.nightBase).v3('u_lpColor', u.lpColor);
    p.f1('u_transparent', u.transparent ? 1 : 0).f1('u_exposure', u.exposure).f2('u_tabScale', this.tabScale[0] * (u.sunScale ?? 1), this.tabScale[1]);
    const sh = this.shade;
    sh.sunEnu = u.sunEnu;
    sh.moonEnu = u.moonEnu;
    sh.sunK = this.tabScale[0] * (u.sunScale ?? 1);
    sh.moonK = u.moonScale > 0 ? u.moonScale * this.tabScale[1] : 0;
    sh.exposure = u.exposure;
    sh.nightBase = u.nightBase;
    sh.lpColor = u.lpColor;
    sh.bandMix = this.bandTex ? this.bandMix : 0;
    sh.ground = null; // until drawGround
    this.overCamera = u.transparent;
    gl.disable(gl.BLEND);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }

  drawGround(setCommon: (p: Program) => void, u: { groundColor: ArrayLike<number>; rimColor: ArrayLike<number>; alpha: number; landscape: boolean }) {
    const gl = this.gl;
    const p = this.ground.use();
    setCommon(p);
    p.v3('u_groundColor', u.groundColor).v3('u_rimColor', u.rimColor).f1('u_groundAlpha', u.alpha).f1('u_landscape', u.landscape ? 1 : 0);
    const gs = this.groundShade;
    gs.color = u.groundColor;
    gs.rim = u.rimColor;
    gs.alpha = u.alpha;
    gs.landscape = u.landscape;
    this.shade.ground = gs;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }
}
